import type { ProviderOptions } from '@promptfoo/types';

import { z } from 'zod';

import { ExampleAppDomainSchema } from './exampleApp';
import { RoleSchema } from './rbac';
import { ServerSettingsKeys } from './serverSettings';
import { UserSchema, UserWithRoleSchema } from './users';

export const CreateTeamSchema = z.object({
  name: z.string().min(1),
  slug: z.string().min(1).optional(),
  createExampleTarget: z.boolean().optional(),
  exampleAppDomain: ExampleAppDomainSchema.optional(),
  probeLimit: z.int().nonnegative().nullable().optional(),
});

export const UpdateTeamSchema = z.object({
  name: z.string().min(1).optional(),
  slug: z.string().min(1).optional(),
  probeLimit: z.int().nonnegative().nullable().optional(),
  canOverrideRedteamProvider: z.boolean().optional(),
});

export const AddTeamMemberSchema = z.object({
  userId: z.string(),
  roleId: z.string().optional(),
});

export const TeamSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  probeLimit: z.number().nullable(),
  canOverrideRedteamProvider: z.boolean(),
  organizationId: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  members: z.array(UserWithRoleSchema),
  targetsCount: z.number().optional(),
});

export const UpdateTeamMemberRoleSchema = z.object({
  roleId: z.string(),
});

export const TeamWithoutMembersSchema = TeamSchema.omit({ members: true });

export const UserWithTeamsSchema = UserSchema.extend({
  teams: z.array(TeamWithoutMembersSchema.extend({ role: RoleSchema })),
});

export type UserWithTeamsDTO = z.infer<typeof UserWithTeamsSchema>;

export type TeamWithoutMembersDTO = z.infer<typeof TeamWithoutMembersSchema>;

export type CreateTeamDTO = z.infer<typeof CreateTeamSchema>;
export type UpdateTeamDTO = z.infer<typeof UpdateTeamSchema>;
export type AddTeamMemberDTO = z.infer<typeof AddTeamMemberSchema>;
export type UpdateTeamMemberRoleDTO = z.infer<typeof UpdateTeamMemberRoleSchema>;
export type UserWithRoleDTO = z.infer<typeof UserWithRoleSchema>;
export type TeamDTO = z.infer<typeof TeamSchema>;

export const TeamProbeUsageStatusSchema = z.enum(['ok', 'approaching', 'over']);
export const TeamProbeUsageSchema = z.object({
  probeLimit: z.number().nullable(),
  probesUsed: z.number(),
  orgProbeLimit: z.number().nullable(),
  orgProbesUsed: z.number().nullable(),
  remaining: z.number().nullable(),
  status: TeamProbeUsageStatusSchema,
  limitingFactor: z.enum(['org', 'team', 'none']),
  contractStartDate: z.string().nullable(),
});

export type TeamProbeUsageDTO = z.infer<typeof TeamProbeUsageSchema>;

export const TeamRedteamProviderSchema = z.union([
  z.string(),
  z.record(z.string(), z.unknown()),
]) as z.ZodType<string | ProviderOptions>;

export const TeamInferenceProviderKeys = [
  ServerSettingsKeys.redteamProvider,
  ServerSettingsKeys.gradingProvider,
  ServerSettingsKeys.guardrailsProvider,
  ServerSettingsKeys.multilingualProvider,
  ServerSettingsKeys.agentProvider,
  ServerSettingsKeys.redteamGenerationProvider,
] as const;

export const TeamInferenceProviderKeySchema = z.enum(TeamInferenceProviderKeys);

export const TeamInferenceProvidersSchema = z.object({
  [ServerSettingsKeys.redteamProvider]: TeamRedteamProviderSchema.optional(),
  [ServerSettingsKeys.gradingProvider]: TeamRedteamProviderSchema.optional(),
  [ServerSettingsKeys.guardrailsProvider]: TeamRedteamProviderSchema.optional(),
  [ServerSettingsKeys.multilingualProvider]: TeamRedteamProviderSchema.optional(),
  [ServerSettingsKeys.agentProvider]: TeamRedteamProviderSchema.optional(),
  [ServerSettingsKeys.redteamGenerationProvider]: TeamRedteamProviderSchema.optional(),
});

export const TeamInferenceProvidersResponseSchema = z.object({
  canOverrideRedteamProvider: z.boolean(),
  providers: TeamInferenceProvidersSchema,
});

export const UpdateTeamInferenceProviderSchema = z.object({
  provider: z.union([TeamRedteamProviderSchema, z.null()]),
});

export const TeamRedteamProviderResponseSchema = z.object({
  canOverrideRedteamProvider: z.boolean(),
  provider: TeamRedteamProviderSchema.nullable(),
});

export const UpdateTeamRedteamProviderSchema = z.object({
  provider: z.union([TeamRedteamProviderSchema, z.null()]),
});

export type TeamRedteamProviderResponse = z.infer<typeof TeamRedteamProviderResponseSchema>;
export type UpdateTeamRedteamProviderRequest = z.infer<typeof UpdateTeamRedteamProviderSchema>;
export type TeamInferenceProviderKey = z.infer<typeof TeamInferenceProviderKeySchema>;
export type TeamInferenceProviders = z.infer<typeof TeamInferenceProvidersSchema>;
export type TeamInferenceProvidersResponse = z.infer<typeof TeamInferenceProvidersResponseSchema>;
export type UpdateTeamInferenceProviderRequest = z.infer<typeof UpdateTeamInferenceProviderSchema>;
